"use client";

// OutageWindow — what innernet says when it cannot reach your memory.
//
// It sits just above the "ask netti" corner, because that is where this product
// already speaks from. No box: a short passage on the ground behind a whisper of
// frost, a hairline above it, and one quiet link. It is uncovered rather than
// entered (law_always_there) — it fades and rises into a place that was already
// there, so nothing lurches.
//
// TWO SENTENCES, NEVER ONE. When something fails we ask /api/health before
// speaking, because "innernet is down" and "that one thing didn't load" are
// different facts and saying the wrong one is its own small betrayal:
//   · the service is down     → name it, point at /status
//   · the service is fine     → say it was this, here, now, and that the memory
//                               itself is untouched
//
// The last line is the one that matters and it is always true: nothing is lost.
// A read failing is not a write happening. Someone staring at a screen that
// won't load their life needs to be told that in the first breath, not the last.

import { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { onUnreachable, serviceIsUp, type OutageSignal } from "../../lib/os/outage";

type Verdict = "checking" | "service" | "local";

/** how long the window rests before it stops asking for attention */
const SETTLE_MS = 14_000;

export default function OutageWindow() {
  const router = useRouter();
  const [signal, setSignal] = useState<OutageSignal | null>(null);
  const [verdict, setVerdict] = useState<Verdict>("checking");
  const [trying, setTrying] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismiss = useCallback(() => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    setSignal(null);
  }, []);

  // "try again" used to be a hard reload, which threw away everything the person
  // had open to fetch the one thing that failed. Now it asks the service again
  // and, if it answers, has the server hand the page its data afresh
  // (router.refresh keeps the shell, the open view and any words being typed).
  // If the service is still down it says so and waits; the window stays.
  const tryAgain = useCallback(async () => {
    if (trying) return;
    setTrying(true);
    setVerdict("checking");
    if (hideTimer.current) clearTimeout(hideTimer.current);
    const up = await serviceIsUp();
    setTrying(false);
    if (up) {
      router.refresh();
      dismiss();
      return;
    }
    setVerdict("service");
    hideTimer.current = setTimeout(() => setSignal(null), SETTLE_MS);
  }, [trying, router, dismiss]);

  useEffect(() => {
    return onUnreachable((s) => {
      setSignal((prev) => {
        // one window, one voice — a burst of failures is still one outage.
        if (prev && s.at - prev.at < 4_000) return prev;
        return s;
      });
      setVerdict("checking");
      void (async () => {
        const up = await serviceIsUp();
        setVerdict(up ? "local" : "service");
      })();
      if (hideTimer.current) clearTimeout(hideTimer.current);
      hideTimer.current = setTimeout(() => setSignal(null), SETTLE_MS);
    });
  }, []);

  useEffect(() => () => { if (hideTimer.current) clearTimeout(hideTimer.current); }, []);

  if (!signal) return null;

  const what = signal.what ?? "your memory";

  return (
    <aside className="outage os-enter-soft" role="status" aria-live="polite">
      <p className="outage-line">
        {verdict === "checking"
          ? `we can't reach ${what} just now — checking whether it's us…`
          : verdict === "service"
            ? `innernet is having trouble reaching itself. it isn't you, and it isn't ${what}.`
            : `${what} didn't load just then. the service is up, so it was this moment, not your memory.`}
      </p>
      <p className="outage-assure">
        nothing has been lost — reading it failed, and reading never changes what
        is kept.
      </p>
      <div className="outage-acts">
        <button className="quiet-link" onClick={() => void tryAgain()} disabled={trying}>
          try again
        </button>
        <Link href="/status" className="quiet-link" prefetch={false}>
          the status page
        </Link>
        <button className="quiet-link outage-dismiss" onClick={dismiss}>
          later
        </button>
      </div>
    </aside>
  );
}
